import type { GameManifest } from "@cloudnest/board-game-runtime";
import { getGameType } from "../game-registry";

export type GameSortMode =
  | "added-default"
  | "added-asc"
  | "added-desc"
  | "name-asc"
  | "name-desc";

export interface GameListFilters {
  readonly twoPlayer: boolean;
  readonly eightPlus: boolean;
  readonly aiSupported: boolean;
}

export const gameSortOptions: readonly {
  readonly value: GameSortMode;
  readonly label: string;
}[] = [
  { value: "added-default", label: "기본(추가 순서)" },
  { value: "added-asc", label: "추가 순서 오름" },
  { value: "added-desc", label: "추가 순서 내림" },
  { value: "name-asc", label: "이름 오름" },
  { value: "name-desc", label: "이름 내림" },
];

export const emptyGameListFilters: GameListFilters = {
  twoPlayer: false,
  eightPlus: false,
  aiSupported: false,
};

export function supportsTwoPlayerGame(game: GameManifest): boolean {
  return game.minPlayers <= 2 && game.maxPlayers >= 2;
}

export function supportsEightPlusGame(game: GameManifest): boolean {
  return game.maxPlayers >= 8;
}

// AI-opponent support is a client-registry concern (config-driven bot roster),
// not part of the server-owned GameManifest — so it is looked up per game type.
export function supportsAiOpponentGame(game: GameManifest): boolean {
  return getGameType(game.gameType)?.supportsAiOpponent === true;
}

export function filterGameTypes(
  games: readonly GameManifest[],
  filters: GameListFilters,
): readonly GameManifest[] {
  const hasActiveFilter =
    filters.twoPlayer || filters.eightPlus || filters.aiSupported;

  if (!hasActiveFilter) {
    return games;
  }

  return games.filter((game) => {
    return (
      (filters.twoPlayer && supportsTwoPlayerGame(game)) ||
      (filters.eightPlus && supportsEightPlusGame(game)) ||
      (filters.aiSupported && supportsAiOpponentGame(game))
    );
  });
}

export function sortGameTypes(
  games: readonly GameManifest[],
  sortMode: GameSortMode,
): readonly GameManifest[] {
  const sortedGames = [...games];

  switch (sortMode) {
    case "added-default":
    case "added-asc":
      return sortedGames.sort((a, b) => getAddedOrder(a) - getAddedOrder(b));
    case "added-desc":
      return sortedGames.sort((a, b) => getAddedOrder(b) - getAddedOrder(a));
    case "name-asc":
      return sortedGames.sort(compareGameName);
    case "name-desc":
      return sortedGames.sort((a, b) => compareGameName(b, a));
  }
}

export function getVisibleGameTypes(
  games: readonly GameManifest[],
  sortMode: GameSortMode,
  filters: GameListFilters,
): readonly GameManifest[] {
  return sortGameTypes(filterGameTypes(games, filters), sortMode);
}

function compareGameName(a: GameManifest, b: GameManifest): number {
  return a.displayName.localeCompare(b.displayName, "ko-KR", {
    numeric: true,
    sensitivity: "base",
  });
}

function getAddedOrder(game: GameManifest): number {
  return getGameType(game.gameType)?.addedOrder ?? Number.MAX_SAFE_INTEGER;
}
